import { useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AlertTriangle, FileX2 } from 'lucide-react';
import { toast } from 'sonner';

import { SettingsWrapper } from './SettingsWrapper';
import { useDeleteDocs } from '@/api/mutations/useDeleteDocs';
import { Button } from '@/components/atoms/new-button';
import { DocumentFilter } from '@/components/molecules/DocumentFilter';
import { MyOptionType } from '@/types';

export const DeleteDocuments = ({ caseId, isReprocessing }: { caseId: string; isReprocessing: boolean }) => {
  const { mutate: deleteDocs, isLoading: isDeleting } = useDeleteDocs(caseId, () => {
    toast.success(`Documents deleting...`);
  });

  const [includedDocs, setIncludedDocs] = useState<Map<string, MyOptionType>>(new Map());

  return (
    <SettingsWrapper>
      <div className="pr-4">
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold">Delete Documents</h2>
            </div>
          </div>
          <p className="text-sm text-gray-500">Remove documents and their associated information from the case.</p>
        </div>
      </div>
      <div className="text-sm text-gray-700">
        <DocumentFilter
          includedDocs={includedDocs}
          excludedDocs={new Map()}
          setIncludedDocs={setIncludedDocs}
          setExcludedDocs={() => {}}
          mode="document-select"
          enabledOnly={true}
        />
      </div>

      <div className="mt-auto rounded-md border border-red-300 bg-red-50 p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Warning: Document Deletion</h3>
            <div className="mt-2 text-sm text-red-700">
              <p className="mb-2">
                This action permanently removes all selected documents and their derived data from your case. This
                deletion cannot be undone.
              </p>
              <ul className="ml-5 list-disc space-y-1 text-xs">
                <li>Facts exclusively sourced from these documents will be permanently deleted</li>
                <li>Kim threads containing these documents or their extracted facts will be removed</li>
                <li>Inconsistencies related to deleted facts will be deleted</li>
                <li>Entities related to deleted facts will be deleted</li>
                <li>Chronologies will be updated to remove any facts extracted from deleted documents</li>
                <li>
                  If the case summary references any deleted information, it will be temporarily unavailable and
                  regenerated. This may take several minutes.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 border-t border-gray-200 pt-4">
        <div className="flex gap-3">
          <Button
            variant="destructive"
            size="sm"
            disabled={includedDocs.size === 0 || isReprocessing}
            onClick={() => deleteDocs(Array.from(includedDocs.keys()))}
          >
            {isDeleting ? (
              <FontAwesomeIcon icon={faCircleNotch} className="fa-spin h-4 w-4" />
            ) : (
              <FileX2 className="h-4 w-4" />
            )}
            {isDeleting ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </div>
        {isReprocessing ? (
          <p className="mt-2 text-left text-xs text-amber-600">
            Case is currently being processed. Updates are temporarily disabled.
          </p>
        ) : (
          <p className="mt-2 text-left text-xs text-gray-500">
            Selected documents will be permanently deleted from the case.
          </p>
        )}
      </div>
    </SettingsWrapper>
  );
};
